import localforage from "localforage";
import { v4 as uuidv4 } from "uuid";
import { pmFetchJson } from "@penmate/react-shared/api/pmFetch";
import {
  captureSessionAttributionFromUrl,
  withSessionAttributionProps,
  withoutSessionAttributionProps,
} from "@/lib/sessionAttribution";

type MetricPayload = Record<string, unknown>;

const errorCategories = ["payment", "validation", "send", "exception", "unknown"] as const;
const messageTypes = ["letter", "emessage", "postcard"] as const;

const metricKey = (key: string): string =>
  key.replace(/([a-z0-9])([A-Z])/g, "$1_$2").replace(/-/g, "_").toLowerCase();

const metricValue = (source: unknown, field: string): unknown => {
  if (!source || typeof source !== "object") return undefined;
  const expected = metricKey(field);
  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    if (metricKey(key) === expected) return value;
  }
  return undefined;
};

const cleanCategory = (value: unknown): string | undefined => {
  const token = errorToken(value);
  if (!token) return undefined;
  return errorCategories.indexOf(token as (typeof errorCategories)[number]) >= 0 ? token : undefined;
};

const messageTypeToken = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const token = value.trim().toLowerCase();
  return messageTypes.indexOf(token as (typeof messageTypes)[number]) >= 0
    ? token
    : undefined;
};

const messageTypeFor = (payload: MetricPayload): string | undefined => {
  const shippingMethod = String(metricValue(payload, "shipping_method") ?? "").trim().toLowerCase();
  const emessageFlag = metricValue(payload, "e_message") ?? metricValue(payload, "emessage");
  if (shippingMethod === "emessage" || emessageFlag === true) return "emessage";
  return (
    messageTypeToken(metricValue(payload, "message_type")) ??
    messageTypeToken(metricValue(payload, "order_type"))
  );
};

const errorToken = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const token = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return token && token.length <= 80 ? token : undefined;
};

const categoryFromLegacyToken = (value: unknown): string | undefined => {
  switch (errorToken(value)) {
    case "payment":
    case "payment_error":
    case "stripe_confirm_failed":
    case "stripe_confirm_missing_fields":
    case "confirm_payment_failed":
      return "payment";
    case "validation":
    case "validation_error":
    case "return_address_error":
    case "return_address_invalid":
      return "validation";
    case "send":
    case "send_error":
    case "send_failed":
      return "send";
    case "exception":
    case "exception_error":
      return "exception";
    default:
      return undefined;
  }
};

const errorTypeFor = (category: string | undefined): string | undefined => {
  if (!category || category === "unknown") return undefined;
  return `${category}_error`;
};

const legacyCategory = (payload: MetricPayload): string | undefined => {
  const rawError = metricValue(payload, "error");
  const tokens = [
    metricValue(payload, "error_kind"),
    metricValue(payload, "error_type"),
    metricValue(payload, "error_code"),
    metricValue(rawError, "kind"),
    metricValue(rawError, "type"),
    metricValue(rawError, "code"),
  ];
  for (const token of tokens) {
    const category = categoryFromLegacyToken(token);
    if (category) return category;
  }
  return undefined;
};

const submitErrorCategory = (payload: MetricPayload): string =>
  cleanCategory(metricValue(payload, "error_category")) ||
  legacyCategory(payload) ||
  "unknown";

const submitErrorPayload = (payload: MetricPayload): MetricPayload => {
  const normalized: MetricPayload = {};
  const messageType = messageTypeFor(payload);
  if (messageType) normalized.message_type = messageType;

  const errorCategory = submitErrorCategory(payload);
  const errorType = errorTypeFor(errorCategory);

  if (errorType) normalized.error_type = errorType;
  normalized.error_category = errorCategory;

  return normalized;
};

const storage = localforage.createInstance({
  name: "__PMdb",
  storeName: "appStore",
});

const ensureDriver = (() => {
  let promise: Promise<void> | null = null;
  return () => {
    if (promise) return promise;
    // Match Ionic Storage ordering as closely as we can on web:
    // sqlite (not available) -> indexeddb -> localstorage (and keep websql as a fallback).
    promise = storage
      .setDriver([localforage.INDEXEDDB, localforage.WEBSQL, localforage.LOCALSTORAGE])
      .then(() => undefined)
      .catch(() => undefined);
    return promise;
  };
})();

let sessionId: number | null = null;
let capturedSessionAttributionHandoffKey: string | null = null;

const sessionAttributionUrlKeys = [
  "source",
  "guide_landing_path",
  "guide_page_segment",
  "guide_source_task",
  "guide_cta_id",
  "guide_cta_position",
  "guide_component",
  "facility_slug",
];

const sessionAttributionHandoffKeyFromUrl = (): string | null => {
  try {
    if (typeof window === "undefined" || !window.location) return null;
    const params = new URL(window.location.href).searchParams;
    // facility_slug rides non-guides prefill links too, so it never marks a
    // handoff by itself; it still takes part in the dedupe key below.
    const hasGuideHandoff =
      params.get("source") === "guides" ||
      sessionAttributionUrlKeys.some(
        (key) => key !== "source" && key !== "facility_slug" && !!params.get(key),
      );
    if (!hasGuideHandoff) return null;
    return sessionAttributionUrlKeys.map((key) => `${key}=${params.get(key) || ""}`).join("&");
  } catch {
    return null;
  }
};

const captureSessionAttributionFromUrlOnce = (): void => {
  const handoffKey = sessionAttributionHandoffKeyFromUrl();
  if (!handoffKey || handoffKey === capturedSessionAttributionHandoffKey) return;
  capturedSessionAttributionHandoffKey = handoffKey;
  captureSessionAttributionFromUrl();
};

const getDeviceId = async (): Promise<string> => {
  await ensureDriver();
  const existing = await storage.getItem<string>("deviceId");
  if (existing && typeof existing === "string" && existing.trim()) return existing;
  const next = uuidv4();
  await storage.setItem("deviceId", next);
  return next;
};

const getTimezone = (payload: MetricPayload): string | undefined => {
  const fromPayload = (payload as any)?.timezone;
  if (typeof fromPayload === "string" && fromPayload.trim()) return fromPayload;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === "string" && tz ? tz : undefined;
  } catch {
    return undefined;
  }
};

export async function trackMetric(
  event: string,
  payload: MetricPayload = {},
  opts: { sessionId?: number | null } = {},
): Promise<void> {
  try {
    const shouldUseSessionAttribution = event !== "add-metrics";
    if (shouldUseSessionAttribution) captureSessionAttributionFromUrlOnce();
    const provided = opts.sessionId;
    if (typeof provided === "number" && Number.isFinite(provided) && provided > 0) {
      sessionId = Math.round(provided);
    }
    if (!sessionId) sessionId = Date.now();
    const device_id = await getDeviceId();
    const timezone = getTimezone(payload);
    const eventPayload = event === "submit-letter-error" ? submitErrorPayload(payload) : payload;
    const trackedPayload = shouldUseSessionAttribution
      ? withSessionAttributionProps(eventPayload)
      : withoutSessionAttributionProps(eventPayload);
    const finalPayload =
      event === "submit-letter-error"
        ? trackedPayload
        : {
            timezone,
            ...trackedPayload,
            device_id,
            session_id: sessionId,
          };
    await pmFetchJson("/metrics", {
      method: "POST",
      body: {
        event,
        payload: finalPayload,
      },
    });
  } catch {
    // Metrics are best-effort; ignore failures.
  }
}
